import { Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { UserRole } from '@/types/auth';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { isMockAuthEnabled } from '@/lib/auth';

const DEBUG_AUTH = import.meta.env.VITE_DEBUG_AUTH === 'true';

interface IProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

export function ProtectedRoute({ children, allowedRoles }: IProtectedRouteProps) {
  const msalIsAuthenticated = useIsAuthenticated();
  // In mock mode, always consider user authenticated
  const isAuthenticated = isMockAuthEnabled || msalIsAuthenticated;
  const location = useLocation();
  const { user, isLoading, rolesResolving } = useCurrentUser();

  // Debug logging in development
  if (DEBUG_AUTH) {
    console.log('[ProtectedRoute] Check for path:', location.pathname);
    console.log('[ProtectedRoute] isMockAuthEnabled:', isMockAuthEnabled);
    console.log('[ProtectedRoute] msalIsAuthenticated:', msalIsAuthenticated);
    console.log('[ProtectedRoute] isAuthenticated:', isAuthenticated);
    console.log('[ProtectedRoute] isLoading:', isLoading);
    console.log('[ProtectedRoute] user:', user);
    console.log('[ProtectedRoute] user roles:', user?.roles);
    console.log('[ProtectedRoute] allowedRoles:', allowedRoles);
  }

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" label="Checking authentication..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    if (DEBUG_AUTH) {
      console.log('[ProtectedRoute] Not authenticated, redirecting to login');
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if roles are specified
  if (allowedRoles && allowedRoles.length > 0) {
    const hasRequiredRole = user?.roles.some((role) =>
      allowedRoles.includes(role)
    );

    // Directory-managed roles arrive via /api/me and may not exist in the
    // token at all — never deny access until they have resolved.
    if (!hasRequiredRole && rolesResolving) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="lg" label="Checking permissions..." />
        </div>
      );
    }

    if (import.meta.env.DEV) {
      if (DEBUG_AUTH) {
        console.log('[ProtectedRoute] hasRequiredRole:', hasRequiredRole);
      }
      if (user?.roles) {
        user.roles.forEach((role) => {
          if (DEBUG_AUTH) {
            console.log(
              `[ProtectedRoute] Checking role "${role}" against allowed:`,
              allowedRoles.includes(role),
            );
          }
        });
      }
    }

    if (!hasRequiredRole) {
      if (DEBUG_AUTH) {
        console.log('[ProtectedRoute] Access denied, redirecting to /unauthorized');
      }
      return <Navigate to="/unauthorized" replace />;
    }
  }

  if (DEBUG_AUTH) {
    console.log('[ProtectedRoute] Access granted for path:', location.pathname);
  }

  return <>{children}</>;
}



